<template>
    <main class="wrapper" :class="{ withBorder: full }">
        <section v-if="forecastsByItem.length">
            <h1 class="title" v-if="full">Forecast</h1>
            <div :class="{ row: full }">
                <div class="col-lg-2" v-if="full">
                    <div class="selector">
                        <div class="head">select a forecast</div>
                        <div class="body">
                            <div
                                v-for="forecast in forecastsByItem"
                                :key="forecast.id"
                                @click="selectForecast(forecast)"
                                :class="{ selected: selectedForecast && selectedForecast.id === forecast.id }"
                                class="item"
                            >
                                {{ forecast.reference }} - {{ forecast.name }}
                            </div>
                        </div>
                    </div>
                </div>
                <div :class="{ 'col-lg-10': full }">
                    <loader :loading="isLoading" v-if="isLoading"></loader>
                    <ForecastTable v-if="selectedForecast && this.forecastWindows">
                        <template slot="head-row-0">
                            <th :colspan="selectedForecast.recurrenceAmount + 2 + (hasPreseries ? 1 : 0)" style="padding: 0">
                                <ForecastNameBanner :forecast="selectedForecast" />
                            </th>
                        </template>
                        <template slot="body">
                            <tbody>
                                <tr>
                                    <td></td>
                                    <td v-if="hasPreseries" class="preseries-header">
                                        <span>
                                            {{ getDateByFormat(selectedForecast.preseriesDate, "MM") }} <br />
                                            {{ getDateByFormat(selectedForecast.preseriesDate, "YYYY") }} <br />
                                            PRESERIES
                                        </span>
                                    </td>
                                    <td v-for="recurrence in selectedForecastInfos.recurrenceAmount" v-bind:key="`rep_amount_${recurrence}`">
                                        <span v-if="selectedForecastInfos.datesRecurrence.length">
                                            {{ getDateByFormat(selectedForecastInfos.datesRecurrence[recurrence - 1], "MM") }} <br />
                                            {{ getDateByFormat(selectedForecastInfos.datesRecurrence[recurrence - 1], "YYYY") }}
                                        </span>
                                    </td>
                                </tr>
                                <tr v-for="(forecastWindow, index) in forecastWindows" :key="forecastWindow.id" :class="{ odd: index % 2 === 0 }">
                                    <td class="zoneName">{{ forecastWindow.zone.name }}</td>
                                    <td v-if="hasPreseries" class="preseries-cell">
                                        {{ preseriesQuantityForWindow(forecastWindow) }}
                                    </td>
                                    <td
                                        v-for="(quantity, qIndex) in forecastWindow.quantities.filter(q => !q.isPreseries && q.recurrence > 0)"
                                        v-if="quantity.item === itemId"
                                        :key="`${forecastWindow.id}_${qIndex}`"
                                    >
                                        {{ quantity.quantity || 0 }}
                                    </td>
                                    <td class="zoneTotal">{{ amountPerForecastWindow(forecastWindow) }}</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td v-if="hasPreseries" class="preseries-cell total">
                                        {{ totalPreseries }}
                                    </td>
                                    <td v-for="recurrence in selectedForecast.recurrenceAmount" :key="`recurrence-cal-${recurrence}`">
                                        {{ amountPerRecurrence(recurrence) }}
                                    </td>
                                    <td class="total">Total: {{ totalAmount }}</td>
                                </tr>
                            </tbody>
                        </template>
                    </ForecastTable>
                </div>
            </div>
        </section>
        <section v-else>No associated forecasts</section>
    </main>
</template>

<script>
import ForecastTable from "@/components/forecasts/elements/table/ForecastTable";
import forecastsService from "@/components/forecasts/forecastsService";
import { mapActions, mapGetters } from "vuex";
import loader from "@/components/loader/loader";
import { FORECAST_STATUS } from "@/components/forecasts/constants";
import ForecastNameBanner from "@/components/forecasts/elements/general/ForecastNameBanner";
import { getQuantitiesForForecastWindow, getQuantitiesForForecastWindows } from "@/components/forecasts/utils/utilis";

export default {
    props: {
        itemId: {
            type: Number,
            required: true
        },
        forecast: {
            type: Object,
            required: false
        },
        embedded: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        forecastWindows: null,
        selectedForecast: null,
        selectedForecastInfos: null,
        isLoading: false,
        full: true
    }),
    components: {
        ForecastNameBanner,
        ForecastTable,
        loader
    },
    async created() {
        if (this.forecast) {
            this.selectForecast(this.forecast);
            this.full = false;
            return;
        }
        await this.populate();
        if (undefined !== this.$route.query.selectedForecast) {
            let forecastId = parseInt(this.$route.query.selectedForecast);
            let forecast = this.forecasts.find(fc => fc.id === forecastId);
            if (forecast) {
                this.selectForecast(forecast);
            }
        }
    },
    methods: {
        ...mapActions("forecast", { populate: "populate" }),
        async fetchForecastWindows(forecast) {
            this.isLoading = true;
            this.forecastWindows = [];
            this.forecastWindows = await forecastsService.getForecastWindowsForForecast(this.selectedForecast.id);
            this.isLoading = false;
        },
        selectForecast(forecast) {
            if (this.selectedForecast && this.selectedForecast.id === forecast.id) {
                return;
            }
            this.selectedForecast = forecast;
            this.selectedForecastInfos = this.forecasts.find(f => f.id === this.selectedForecast.id);
            this.fetchForecastWindows(forecast);
        },
        amountPerForecastWindow(forecastWindow) {
            return getQuantitiesForForecastWindow(this.itemId, forecastWindow);
        },
        amountPerRecurrence(recurrenceAmount) {
            return this.forecastWindows.reduce((amount, fcw) => {
                let currentAmount = fcw.quantities
                    .filter(q => q.item === this.itemId && q.recurrence === recurrenceAmount && !q.isPreseries)
                    .reduce((a, b) => a + b.quantity, 0);
                return amount + currentAmount;
            }, 0);
        },
        preseriesQuantityForWindow(forecastWindow) {
            let preseries = forecastWindow.quantities.find(q => q.item === this.itemId && q.isPreseries);
            return preseries ? preseries.quantity || 0 : 0;
        }
    },
    computed: {
        hasPreseries() {
            return !!(this.selectedForecast && this.selectedForecast.preseriesDate);
        },
        totalPreseries() {
            if (!this.forecastWindows) return 0;
            return this.forecastWindows.reduce((total, fcw) => {
                return total + this.preseriesQuantityForWindow(fcw);
            }, 0);
        },
        ...mapGetters("forecast", { forecastsAll: "all" }),
        forecasts() {
            return this.forecastsAll.filter(fc => fc.status !== FORECAST_STATUS.DRAFT);
        },
        forecastsByItem() {
            return this.forecasts.filter(f => f.items.find(i => i.id === this.itemId));
        },
        totalAmount() {
            if (!this.forecastWindows) {
                return "";
            }
            return getQuantitiesForForecastWindows(this.itemId, this.forecastWindows);
        }
    }
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/css/themes";
.wrapper {
    text-transform: uppercase;

    padding-bottom: 15px;
    margin: 10px 0;
    .withBorder {
        border-top: #282425 2px solid;
        border-bottom: #282425 2px solid;
    }
}
.selected {
    color: #b6041a;
}
.zoneTotal {
    background-color: #d0d0d0;
    text-align: right;
}
.odd .zoneTotal {
    background-color: #a8a6a7;
}
table tr.odd {
    background-color: #c5c1c3;
}
.selector {
    margin-bottom: 15px;
    .head {
        padding: 7px;
        color: #ffffff;
        background-color: #bababa;
    }
    .body {
        color: #bababa;
        background-color: #f2f2f2;
        padding: 15px 15px;
        .item:not(.selected) {
            cursor: pointer;
        }
    }
}
.total {
    font-size: 1.3em;
    font-weight: bold;
    text-align: right;
}
.zoneName {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &:hover {
        overflow: visible;
        z-index: 1000;
    }
}
.preseries-header {
    background-color: #ffa7b3;
    text-align: center;
    font-weight: bold;
    text-transform: uppercase;
}
.preseries-cell {
    background-color: #ffe0e5;
    text-align: center;
}
.odd .preseries-cell {
    background-color: #f5c0c8;
}
</style>



// WEBPACK FOOTER //
// ItemQuantities.vue?6b4d20f2